import React, { useEffect, useState } from "react";
import { useJobStore } from "../store/Jobs";
import { useCentralStore } from "../store/Central";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { usePatientStore } from "../store/Patient";

export const PatientMatchingDialog = () => {
	const pendingDecisions = useJobStore(
		(state) => state.pendingPatientDecisions
	);
	const resolvePatientDecision = useJobStore(
		(state) => state.resolvePatientDecision
	);
	const clientsLookup = useCentralStore((state) => state.clientsLookup);
	const patientNameFn = usePatientStore((state) => state.patientName);
	const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
		null
	);
	const [editedPatientData, setEditedPatientData] = useState<any>(null);

	const currentDecision = pendingDecisions[0]; // Process one at a time

	// Initialize edited data when decision changes
	useEffect(() => {
		if (currentDecision?.reason === "constraint_violation") {
			setEditedPatientData(currentDecision.patientFields || {});
		}
	}, [currentDecision]);

	if (!currentDecision) {
		return null;
	}

	const handleSelect = () => {
		if (selectedPatientId) {
			resolvePatientDecision(
				currentDecision.ruleId,
				"select",
				selectedPatientId
			);
			setSelectedPatientId(null);
		}
	};

	const handleCreate = () => {
		resolvePatientDecision(currentDecision.ruleId, "create");
		setSelectedPatientId(null);
	};

	const handleSaveEdited = () => {
		if (editedPatientData) {
			// Pass edited data to resolvePatientDecision instead of mutating readonly state
			resolvePatientDecision(
				currentDecision.ruleId,
				"create",
				undefined,
				editedPatientData
			);
			setEditedPatientData(null);
		}
	};

	const handleSkip = () => {
		resolvePatientDecision(currentDecision.ruleId, "skip");
		setSelectedPatientId(null);
		setEditedPatientData(null);
	};

	const getReasonText = (reason: string) => {
		switch (reason) {
			case "multiple_matches":
				return "Mehrere passende Patienten gefunden";
			case "pattern_match":
				return "Ähnlicher Patient gefunden";
			case "different_client":
				return "Patient von anderem Kunden gefunden";
			case "different_client_pattern":
				return "Ähnlicher Patient von anderem Kunden gefunden";
			case "constraint_violation":
				return "Fehler beim Erstellen des Patienten";
			default:
				return "Patientenzuordnung erforderlich";
		}
	};

	const getMatchTypeBadge = (matchType: string) => {
		if (matchType === "exact") {
			return <Badge className="bg-green-500">Exakt</Badge>;
		} else if (matchType === "pattern") {
			return <Badge className="bg-yellow-500">Ähnlich</Badge>;
		}
		return null;
	};

	return (
		<Dialog open={true} onOpenChange={handleSkip} modal>
			<DialogContent
				className="max-w-2xl max-h-[80vh] overflow-y-auto"
				overlayClassName="bg-black/50"
			>
				<DialogHeader>
					<DialogTitle>Patientenzuordnung erforderlich</DialogTitle>
					<DialogDescription>
						{getReasonText(currentDecision.reason)}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 my-4">
					<div className="text-sm text-muted-foreground">
						Regel:{" "}
						<span className="font-semibold">
							{currentDecision.ruleName}
						</span>
					</div>

					{/* Display submitted patient data */}
					{currentDecision.patientFields &&
						Object.keys(currentDecision.patientFields).length >
							0 && (
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<div className="text-sm font-semibold text-blue-900 mb-3">
									Eingereichte Patientendaten
								</div>
								<div className="grid grid-cols-2 gap-3 text-sm">
									{currentDecision.patientFields
										.first_name && (
										<div>
											<span className="text-muted-foreground">
												Vorname:
											</span>{" "}
											<span className="font-medium">
												{
													currentDecision
														.patientFields
														.first_name
												}
											</span>
										</div>
									)}
									{currentDecision.patientFields
										.last_name && (
										<div>
											<span className="text-muted-foreground">
												Nachname:
											</span>{" "}
											<span className="font-medium">
												{
													currentDecision
														.patientFields.last_name
												}
											</span>
										</div>
									)}
									{currentDecision.patientFields.code && (
										<div>
											<span className="text-muted-foreground">
												Code:
											</span>{" "}
											<span className="font-medium">
												{
													currentDecision
														.patientFields.code
												}
											</span>
										</div>
									)}
									{currentDecision.patientFields
										.birth_date && (
										<div>
											<span className="text-muted-foreground">
												Geboren:
											</span>{" "}
											<span className="font-medium">
												{new Date(
													currentDecision.patientFields.birth_date
												).toLocaleDateString("de-DE")}
											</span>
										</div>
									)}
								</div>
							</div>
						)}

					{currentDecision.reason === "constraint_violation" ? (
						<div className="space-y-4">
							<div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
								{currentDecision.constraintError ||
									"Ein Fehler ist beim Erstellen des Patienten aufgetreten"}
							</div>
							<div className="space-y-3">
								<div>
									<Label htmlFor="edit-code">Code *</Label>
									<Input
										id="edit-code"
										value={editedPatientData?.code || ""}
										onChange={(e) =>
											setEditedPatientData({
												...editedPatientData,
												code: e.target.value,
											})
										}
										className={
											currentDecision.conflictingField ===
											"code"
												? "border-red-500"
												: ""
										}
										data-testid="edit-patient-code"
									/>
									{currentDecision.conflictingField ===
										"code" && (
										<div className="text-xs text-red-600 mt-1">
											Dieses Feld verursacht den Konflikt
										</div>
									)}
								</div>
								<div>
									<Label htmlFor="edit-first-name">
										Vorname
									</Label>
									<Input
										id="edit-first-name"
										value={
											editedPatientData?.first_name || ""
										}
										onChange={(e) =>
											setEditedPatientData({
												...editedPatientData,
												first_name: e.target.value,
											})
										}
										className={
											currentDecision.conflictingField ===
											"first_name"
												? "border-red-500"
												: ""
										}
										data-testid="edit-patient-first-name"
									/>
								</div>
								<div>
									<Label htmlFor="edit-last-name">
										Nachname
									</Label>
									<Input
										id="edit-last-name"
										value={
											editedPatientData?.last_name || ""
										}
										onChange={(e) =>
											setEditedPatientData({
												...editedPatientData,
												last_name: e.target.value,
											})
										}
										className={
											currentDecision.conflictingField ===
											"last_name"
												? "border-red-500"
												: ""
										}
										data-testid="edit-patient-last-name"
									/>
								</div>
								<div>
									<Label htmlFor="edit-birth-date">
										Geburtsdatum
									</Label>
									<Input
										id="edit-birth-date"
										type="date"
										value={
											editedPatientData?.birth_date || ""
										}
										onChange={(e) =>
											setEditedPatientData({
												...editedPatientData,
												birth_date: e.target.value,
											})
										}
										className={
											currentDecision.conflictingField ===
											"birth_date"
												? "border-red-500"
												: ""
										}
										data-testid="edit-patient-birth-date"
									/>
								</div>
							</div>
						</div>
					) : (
						currentDecision.matchCount > 0 && (
							<div className="space-y-2">
								<div className="text-sm font-medium">
									{currentDecision.matchCount}{" "}
									{currentDecision.matchCount === 1
										? "Patient gefunden"
										: "Patienten gefunden"}
									:
								</div>
								<div className="space-y-2 max-h-96 overflow-y-auto">
									{currentDecision.matchResults.map(
										(patient: any, index: number) => {
											const clientName = patient.client_id
												? getClientDisplayName(
														clientsLookup[
															patient.client_id
														]
													) || "Unbekannt"
												: "Unbekannt";
											return (
												<div
													key={index}
													className={`border rounded-lg p-3 cursor-pointer transition-colors ${
														selectedPatientId ===
														patient.id
															? "border-primary bg-primary/5"
															: "border-border hover:border-primary/50"
													}`}
													onClick={() =>
														setSelectedPatientId(
															patient.id
														)
													}
													data-testid={`patient-option-${index}`}
													aria-label={`Patient ${patientNameFn(
														patient
													)} auswählen`}
												>
													<div className="flex items-center justify-between">
														<div>
															<div className="font-medium">
																{patientNameFn(
																	patient
																)}
															</div>
															<div className="text-sm text-muted-foreground space-x-2">
																{patient.code && (
																	<span>
																		Code:{" "}
																		{
																			patient.code
																		}
																	</span>
																)}
																{patient.birth_date && (
																	<span>
																		Geboren:{" "}
																		{new Date(
																			patient.birth_date
																		).toLocaleDateString(
																			"de-DE"
																		)}
																	</span>
																)}
															</div>
															<div className="text-xs text-muted-foreground mt-1">
																Kunde:{" "}
																{clientName}
															</div>
															{patient.client_id &&
																currentDecision.isDifferentClient && (
																	<div className="text-xs text-orange-600 mt-1">
																		⚠️
																		Anderer
																		Kunde
																	</div>
																)}
														</div>
														<div className="flex gap-2">
															{getMatchTypeBadge(
																currentDecision.matchType
															)}
														</div>
													</div>
												</div>
											);
										}
									)}
								</div>
							</div>
						)
					)}
				</div>

				<DialogFooter className="flex gap-2 sm:justify-between">
					{currentDecision.reason === "constraint_violation" ? (
						<>
							<Button
								onClick={handleSkip}
								variant="outline"
								data-testid="skip-patient-button"
								aria-label="Abbrechen und Auftrag manuell zuweisen"
							>
								Abbrechen
							</Button>
							<Button
								onClick={handleSaveEdited}
								data-testid="save-edited-patient-button"
								aria-label="Korrigieren und erneut versuchen"
							>
								Korrigieren und erneut versuchen
							</Button>
						</>
					) : (
						<>
							<Button
								onClick={handleSkip}
								data-testid="skip-patient-button"
								aria-label="Überspringen und manuell zuweisen"
							>
								Überspringen
							</Button>
							<div className="flex gap-2">
								<Button
									onClick={handleCreate}
									data-testid="create-patient-button"
									aria-label="Neuen Patienten erstellen"
								>
									Neuen Patienten erstellen
								</Button>
								<Button
									onClick={handleSelect}
									disabled={!selectedPatientId}
									data-testid="select-patient-button"
									aria-label="Ausgewählten Patienten zuweisen"
								>
									Patient auswählen
								</Button>
							</div>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
